import { useRef, useState } from "react";
import { css } from "@emotion/react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import { ukLocalFormatDateTime } from "../../../util.js";
import { WorkItemStatusBox } from "../../Caseload/WorkItemStatusBox.js";
import type {
  MenuOptionsType,
  WorkItemStatusSummary,
} from "~/components/design-system/index.js";
import { MenuFlyout } from "~/components/design-system/index.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";
import { SubContainer } from "~/components/layout/styleWrappers.js";

export type FormDataChip = {
  label: string;
  /** Hover text explaining what the chip means. */
  tooltip?: string;
  /**
   * Draws the chip as something to act on rather than merely note. Filled from
   * the same token as the in-progress and amend-in-progress status pills, so
   * the one yellow means "needs your attention" wherever it appears on a row.
   */
  tone?: "warning";
};

type FormRowProps = {
  formDataChips?: (string | FormDataChip)[];
  menuOptions: MenuOptionsType[];
  heading: string;
  subHeading: string;
  summary?: WorkItemStatusSummary;
  onClick?: () => Promisable<void>;
  lastMerged?: string;
  formUploadedAt?: string;
  // Presents the card as inactive: muted so it reads as context-only and not a
  // live choice. The card's actions stay usable — this is purely a visual cue.
  greyedOut?: boolean;
};

export function FormRow({
  formDataChips,
  menuOptions,
  heading,
  subHeading,
  summary,
  onClick,
  lastMerged,
  formUploadedAt,
  greyedOut = false,
}: FormRowProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const cardRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const styles = {
    listItem: css`
      &:hover {
        background-color: ${theme.palette.background.default};
        cursor: pointer;
        & :first-of-type {
          color: ${theme.palette.primary.main};
          fill: ${theme.palette.primary.main};
        }
      }
      padding-bottom: 1px;
      padding-top: 0;
    `,
    subtitle: css`
      font-size: ${theme.spacing(1.625)};
      font-weight: 400;
      color: ${theme.palette.primary.hint};
    `,
    container: css`
      @media screen and (max-width: ${theme.breakpoints.values.md}px) {
        width: 100%;
      }
      @media screen and (min-width: ${theme.breakpoints.values.md}px) {
        width: 50%;
      }

      border-left: ${theme.spacing(0.4)} solid
        ${greyedOut ? theme.palette.text.disabled : theme.palette.primary.main};
      padding-top: ${theme.spacing(0.5)};
      padding-bottom: ${theme.spacing(0.5)};
      align-content: center;
      padding-left: ${theme.spacing(1.2)};
      padding-right: ${theme.spacing(2)};
    `,
    root: css`
      flex-direction: row;
      margin-bottom: ${theme.spacing(1)};
    `,
    title: css`
      font-size: ${theme.spacing(2.75)};
      font-weight: 400;
      color: ${theme.palette.text.primary};
      text-align: left;
    `,
    icon: css`
      padding: 0;
      color: ${theme.palette.text.secondary};
      margin-right: ${theme.spacing(0.7)};
      &:hover {
        color: ${theme.palette.primary.main};
      }
    `,
  };

  // When greyed, dim the descriptive content but leave the status summary at
  // full strength — it explains why the form is unusable and must stay legible.
  const dimmedWhenGreyed = greyedOut ? { opacity: 0.6 } : undefined;

  return (
    <Box
      sx={{ marginBottom: "1em", width: "100%" }}
      onClick={
        onClick &&
        (() => {
          void onClick();
        })
      }
    >
      <SubContainer
        styleAsClickable={!!onClick}
        sx={
          greyedOut
            ? {
                backgroundColor: "grey.100",
                boxShadow: "none",
                // A greyed card should recede, not gain a heavier outline than a
                // live card.
                borderColor: "grey.200",
              }
            : undefined
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "start", md: "center" },
          }}
        >
          <Box
            css={styles.container}
            data-testid="box-id"
            sx={dimmedWhenGreyed}
          >
            <Box
              css={styles.root}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h2"
                css={styles.title}
                data-testid="title-id"
              >
                <span>{heading}</span>
              </Typography>
            </Box>
            <Typography css={styles.subtitle}>{subHeading}</Typography>

            {!!lastMerged && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  css={css`
                    font-size: ${theme.spacing(1.625)};
                    font-weight: 600;
                    color: ${theme.palette.error.main};
                    font-style: italic;
                    margin-top: ${theme.spacing(0.5)};
                  `}
                >
                  {t("common.merge")}: {ukLocalFormatDateTime(lastMerged)}
                </Typography>
                <Tooltip
                  title="Form(s) started in another patient, and was merged into this patient"
                  arrow
                >
                  <IconButton>
                    <InfoOutlinedIcon
                      sx={{ color: theme.palette.error.main }}
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {!!formUploadedAt && (
              <Chip
                sx={{ marginTop: 1 }}
                color={"primary"}
                label={`Form Uploaded: ${ukLocalFormatDateTime(
                  formUploadedAt,
                )}`}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "grid",
              // The Summary should read as a column down the list, so its
              // position must not depend on form-data width. Every row shares
              // this column template, so the Summary lands in the same track in
              // each. minmax(0, …) lets tracks shrink rather than overflow on
              // wide, uncapped rows.
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1fr) minmax(0, 1fr) auto",
              },
              alignItems: { xs: "flex-start", md: "center" },
              gap: 1,
              marginTop: { xs: 1, md: 0 },
            }}
          >
            <Box sx={dimmedWhenGreyed}>
              {!!formDataChips?.length && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    css={css`
                      font-size: ${theme.spacing(1.625)};
                      color: ${theme.palette.primary.hint};
                      margin-bottom: ${theme.spacing(0.5)};
                    `}
                  >
                    Form data
                  </Typography>
                  <Box
                    sx={{
                      gap: 1,
                      display: "flex",
                      flexWrap: "wrap",
                      maxWidth: "500px",
                      fontSize: 13,
                    }}
                  >
                    {formDataChips.map((chip) => {
                      const label =
                        typeof chip === "string" ? chip : chip.label;
                      const tooltip =
                        typeof chip === "string" ? undefined : chip.tooltip;
                      const tone =
                        typeof chip === "string" ? undefined : chip.tone;

                      return (
                        <Chip
                          sx={{
                            backgroundColor:
                              tone === "warning"
                                ? "statuses.pending"
                                : "common.lightGray",
                          }}
                          key={label}
                          label={
                            tooltip ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {label}
                                <TooltipInfoBox
                                  title={tooltip}
                                  icon={
                                    <InfoOutlinedIcon
                                      sx={{
                                        fontSize: "1rem",
                                        ml: 0.5,
                                        display: "block",
                                      }}
                                    />
                                  }
                                />
                              </Box>
                            ) : (
                              label
                            )
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
            <Box>
              {summary && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <WorkItemStatusBox
                    color={summary.textColour ?? theme.palette.common.black}
                    backgroundColor={summary.colour}
                    headingText={"Summary"}
                    mainText={
                      isMobile && summary.shortStatusText
                        ? summary.shortStatusText
                        : summary.statusText
                    }
                    align="flex-start"
                    tooltip={summary.tooltip}
                    singleLine
                  />
                  {summary.secondaryPill && (
                    <WorkItemStatusBox
                      color={summary.secondaryPill.textColour}
                      backgroundColor={summary.secondaryPill.colour}
                      headingText={""}
                      mainText={
                        isMobile
                          ? summary.secondaryPill.shortText
                          : summary.secondaryPill.text
                      }
                      align="flex-start"
                      hideHeadingText
                      singleLine
                    />
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: { md: "flex-end", xs: "flex-start" },
                alignItems: "center",
              }}
            >
              <Box
                sx={{ display: "flex" }}
                ref={cardRef}
                onClick={(e) => {
                  if (menuOptions.length >= 1) {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }
                }}
              >
                {!!menuOptions.length && (
                  <Box sx={{ marginTop: { xs: 2, md: 0 } }}>
                    <MenuFlyout
                      cardRef={cardRef}
                      options={menuOptions}
                      isOpen={isOpen}
                      onClose={() => {
                        setIsOpen(false);
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </SubContainer>
    </Box>
  );
}
